// src/CcpAdminUsers/Lists/Lists.js
import React, { Fragment, useEffect, useState } from "react";
import GridToolbarNoExport from "../../common/GridToolbarNoExport";
import AppDataGrid from "../../common/AppDataGrid";
import {
  IconButton,
  Snackbar,
  Button,
  Dialog,
  Paper,
  Alert,
  TextField,
  InputAdornment,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import {
  db,
  massUpdateCcpSchoolFlag,
  deleteUser,
} from "../../../config/firebase";
import EditModal from "../EditForm/EditModal";
import AddForm from "../AddForm/AddForm";
import PasswordResetModal from "../PasswordReset/PasswordResetModal";
import ConfirmDeleteDialog from "../../common/ConfirmDeleteDialog";

const Lists = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openEdit, setOpenEdit] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openPasswordReset, setOpenPasswordReset] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [searchText, setSearchText] = useState("");

  const handleMassUpdate = async () => {
    try {
      await massUpdateCcpSchoolFlag(); // Call the function from firebase.js
      setSnackbarSeverity("success");
      setSnackbarMessage("Mass update successful!");
      fetchUsers(); // Refetch users to update the grid
    } catch (error) {
      console.error("Error during mass update:", error);
      setSnackbarSeverity("error");
      setSnackbarMessage("Mass update failed. Check console for details.");
    } finally {
      setSnackbarOpen(true);
    }
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const fetchUsers = async () => {
    const q = query(
      collection(db, "users"),
      where("schoolUser", "==", "yes"),
      orderBy("school_name"),
    );
    const querySnapshot = await getDocs(q);
    const usersList = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setUsers(usersList);
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = users.filter((user) => {
    const searchValue = searchText.toLowerCase();

    return (
      user.school_name.toLowerCase().includes(searchValue) ||
      user.name.toLowerCase().includes(searchValue) ||
      user.email.toLowerCase().includes(searchValue) ||
      user.phone.toLowerCase().includes(searchValue) ||
      user.title.toLowerCase().includes(searchValue) ||
      user.userType.toLowerCase().includes(searchValue) ||
      String(user.school_id).toLowerCase().includes(searchValue) ||
      user.id.toLowerCase().includes(searchValue)
    );
  });

  const handleEditOpen = (user) => {
    setSelectedUser(user);
    setOpenEdit(true);
  };

  const handleEditClose = () => {
    setOpenEdit(false);
  };

  const handleAddOpen = () => {
    setOpenAdd(true);
  };

  const handleAddClose = () => {
    setOpenAdd(false);
  };

  const handlePasswordResetOpen = (user) => {
    setSelectedUser(user);
    setOpenPasswordReset(true);
  };

  const handlePasswordResetClose = () => {
    setOpenPasswordReset(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleEditSuccess = () => {
    setSnackbarMessage("User has been updated");
    setSnackbarOpen(true);
    handleEditClose();
    // Fetch updated users
    fetchUsers();
  };

  const handleAddSuccess = () => {
    setSnackbarMessage("New user has been created successfully");
    setSnackbarOpen(true);
    handleAddClose();
    // Fetch updated users
    fetchUsers();
  };

  const handlePasswordResetSuccess = () => {
    setSnackbarMessage("Password has been updated successfully");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    handlePasswordResetClose();
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;

    setDeleting(true);
    try {
      // Use the helper which ensures auth token is attached
      const result = await deleteUser(selectedUser.id);

      if (result && result.success) {
        setSnackbarSeverity("success");
        setSnackbarMessage("User deleted successfully");
        await fetchUsers();
      } else {
        setSnackbarSeverity("error");
        setSnackbarMessage(
          result && result.message ? result.message : "Failed to delete user",
        );
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      setSnackbarSeverity("error");
      // If error is a Functions HttpsError, message may be under error.message or error.code
      setSnackbarMessage(
        error && error.message ? error.message : "Failed to delete user",
      );
    } finally {
      setSnackbarOpen(true);
      setOpenDeleteDialog(false);
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
  };

  const columns = [
    { field: "school_name", headerName: "School", flex: 1 },
    { field: "name", headerName: "Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1 },
    { field: "phone", headerName: "Phone", flex: 1 },
    { field: "title", headerName: "Title", flex: 1 },
    { field: "userType", headerName: "User Type", flex: 1 },
    { field: "school_id", headerName: "School", flex: 1 },

    { field: "id", headerName: "id", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <Fragment>
          <IconButton onClick={() => handleEditOpen(params.row)}>
            <EditIcon className="edit-icon" />
          </IconButton>
          <IconButton onClick={() => handlePasswordResetOpen(params.row)}>
            <LockIcon style={{ color: "#1976d2" }} />
          </IconButton>
          <IconButton onClick={() => handleDeleteClick(params.row)}>
            <DeleteIcon style={{ color: "red" }} />
          </IconButton>
        </Fragment>
      ),
    },
  ];

  return (
    <Fragment>
      <TextField
        label="Filter"
        variant="outlined"
        value={searchText}
        onChange={handleSearchChange}
        fullWidth
        margin="normal" // Add margin
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon /> {/* Replace with actual search icon component */}
            </InputAdornment>
          ),
        }}
        style={{ marginBottom: "1rem", marginTop: "1rem" }} // Add marginBottom for spacing
      />

      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleAddOpen}
        style={{ marginBottom: 16, marginTop: 10 }}
      >
        Add New User
      </Button>
      {/* Mass Update Button  <Button
        variant="contained"
        color="primary"
        onClick={handleMassUpdate}
        style={{ marginBottom: 16, marginTop: 10 }} // Add styling as needed
      >
        Mass Update
      </Button>*/}
      <Paper mt={2} mb={2} ml={2} mr={2}>
        <AppDataGrid
          sx={{ p: 3 }}
          initialState={{
            columns: {
              columnVisibilityModel: {
                // Hide columns status and traderName, the other columns will remain visible
                id: false,
              },
            },
          }}
          rows={filteredUsers}
          columns={columns}
          loading={loading}
          slots={{ toolbar: GridToolbarNoExport }}
          autosizeOnMount
          autoHeight
          pagination
          pageSize={25}
          pageSizeOptions={[25]}
          headerClassName="custom-header-class"
        />
      </Paper>
      <Dialog open={openEdit} onClose={handleEditClose}>
        <EditModal
          user={selectedUser}
          onSuccess={handleEditSuccess}
          onClose={handleEditClose}
        />
      </Dialog>
      <Dialog open={openAdd} onClose={handleAddClose}>
        <AddForm onSuccess={handleAddSuccess} onClose={handleAddClose} />
      </Dialog>
      <Dialog open={openPasswordReset} onClose={handlePasswordResetClose}>
        <PasswordResetModal
          user={selectedUser}
          onSuccess={handlePasswordResetSuccess}
          onClose={handlePasswordResetClose}
        />
      </Dialog>
      <ConfirmDeleteDialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        deleting={deleting}
        message="Are you sure you want to delete this user?"
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Fragment>
  );
};

export default Lists;
